// @ts-nocheck

import React, { forwardRef, useState, useEffect, useMemo, useCallback } from 'react';
import type { ElementRef } from 'react';
import styled, { css } from 'styled-components';

import { Mymatch } from '../../assets/svg';
import { Target } from '../../client';
import { __ } from '../../i18n/l10n';
import theme from '../../styled-components/dark';
import ArrowButton from '../ArrowButton';
import TopMyMatchCardContainer from '../MatchTopMyMatchCard/MatchTopMyMatchCardContainer';
import Swipe from '../Swipe';

const Wrapper = styled.div`
  padding: 0.5em 0;
`;

const PaginationWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  font-size: 100%;
  margin-top: 0.5em;
`;

const StyledMyMatchIcon = styled(Mymatch)`
  height: 0.6em;
  margin-right: 0.4em;
  margin-bottom: -0.15em;
`;

const ContentWrapper = styled.div`
  position: relative;
  overflow-x: hidden;

  ${({ theme }) => {
    return css`
      @media screen and (max-width: ${theme.breakpoints.md}px) {
        font-size: 0.9em;
      }
    `;
  }}
`;

const StyledSwipe = styled(Swipe)`
  display: flex;
  align-items: flex-end;
  width: calc(100% - 30px);
  opacity: 1;
`;

const PrevButton = styled(ArrowButton)``;
const NextButton = styled(ArrowButton)`
  margin-left: 0.5em;
  font-weight: light;
`;

const Title = styled.div`
  display: flex;
  align-items: center;
  text-transform: none;
  font-size: 1.1em;
  font-weight: 600;
  color: ${theme.Match.GroupHeader.color};
  line-height: 1.1em;
  padding-left: 0.6em;
  margin-bottom: 0.5em;
`;

type MatchTopMyMatchProps = {
  nbTMM: number;
  setIndex: (param: ((prev: number) => number) | number) => void;
  onLoad: ({ width: number, height: number }) => void;
  current: number;
  matchId: number;
  className?: string;
};

const MatchTopMyMatch = forwardRef<MatchTopMyMatchProps, ElementRef<'div'>>(function MatchTopMyMatch(
  { nbTMM, onLoad, setIndex, current, matchId, className = '' },
  ref,
) {
  const [indexToGo, setIndexToGo] = useState(null);

  useEffect(() => {
    // reset indexToGo when it's apply
    if (indexToGo === current) {
      setIndexToGo(null);
    }
  }, [current, indexToGo]);

  const onPrevClick = useCallback(() => {
    if (current > 0) {
      setIndexToGo(current - 1);
    }
  }, [current]);

  const onNextClick = useCallback(() => {
    if (current < nbTMM) {
      setIndexToGo(current + 1);
    }
  }, [nbTMM, current]);

  const TopMyMatchCards = useMemo(
    () =>
      [...new Array(nbTMM).keys()].map((index) => (
        <TopMyMatchCardContainer matchId={matchId} key={index} index={index} />
      )),
    [nbTMM, matchId],
  );

  return (
    <Wrapper>
      <Title>
        <StyledMyMatchIcon />
        {__('most popular')}
      </Title>
      <ContentWrapper className={className} ref={ref}>
        <StyledSwipe
          setIndex={setIndex}
          indexToGo={indexToGo}
          onResize={onLoad}
          fillSpace
          showNextPage
          noScale
          noPadding
          hasSameHeight
          alwaysRenderEvery
          disable={!Target.__MOBILE__}
          gapBetweenCards={10}
          childrenClassName="is-top-my-match"
        >
          {TopMyMatchCards}
        </StyledSwipe>
        {!Target.__MOBILE__ ? (
          <PaginationWrapper>
            <PrevButton angle={180} onClick={onPrevClick} disabled={current <= 0} />
            <NextButton angle={0} onClick={onNextClick} disabled={current >= nbTMM - 1} />
          </PaginationWrapper>
        ) : null}
      </ContentWrapper>
    </Wrapper>
  );
});

export default React.memo<MatchTopMyMatchProps>(MatchTopMyMatch);
